import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { AppShell } from './components/AppShell';
import { ErrorState, Loading } from './components/Common';
import { currentSession, supabase } from './lib/auth';
import { initialContentSummary, loadContent, loadContentSummary } from './lib/content';
import type { ContentBundle, ContentSummary } from './lib/types';
import { HomePage } from './pages/Home';
import { TrackPage } from './pages/Track';
import { StudySetupPage } from './pages/StudySetup';
import { ExamSetupPage } from './pages/ExamSetup';
import { ArticleSubmissionPage } from './pages/ArticleSubmission';
import { ContactPage, NotFoundPage, StaticPage, SupportPage } from './pages/StaticPages';
import { localStore } from './lib/storage';
import { startAutomaticSync } from './lib/auto-sync';
import { LegalArchivePage, LegalPage } from './pages/Legal';
import { api } from './lib/api';
import { Seo } from './components/Seo';
import { halsArticle, motorboatExamArticle } from '../shared/articles';

type AccountAccess = { userId: string; state: 'active' | 'inactive' | 'error' };
const GuidesPage = lazy(() => import('./pages/Guides').then(module => ({ default: module.GuidesPage })));
const HalsArticlePage = lazy(() => import('./pages/HalsArticle').then(module => ({ default: module.HalsArticlePage })));
const MotorboatExamArticlePage = lazy(() => import('./pages/MotorboatExamArticle').then(module => ({ default: module.MotorboatExamArticlePage })));
const AccountPage = lazy(() => import('./pages/Account').then(module => ({ default: module.AccountPage })));
const AuthCallbackPage = lazy(() => import('./pages/Account').then(module => ({ default: module.AuthCallbackPage })));
const DashboardPage = lazy(() => import('./pages/Account').then(module => ({ default: module.DashboardPage })));
const DeletionPage = lazy(() => import('./pages/Account').then(module => ({ default: module.DeletionPage })));
const LoginPage = lazy(() => import('./pages/Account').then(module => ({ default: module.LoginPage })));
const OwnerPage = lazy(() => import('./pages/Owner').then(module => ({ default: module.OwnerPage })));
const ContributorPage = lazy(() => import('./pages/Contributor').then(module => ({ default: module.ContributorPage })));
const StudySessionPage = lazy(() => import('./pages/Study').then(module => ({ default: module.StudySessionPage })));
const StudyDonePage = lazy(() => import('./pages/Study').then(module => ({ default: module.StudyDonePage })));
const ExamSessionPage = lazy(() => import('./pages/Exam').then(module => ({ default: module.ExamSessionPage })));
const ExamResultPage = lazy(() => import('./pages/Exam').then(module => ({ default: module.ExamResultPage })));

class RouteErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed ? <ErrorState message="Nie udało się wczytać tej części serwisu." retry={() => window.location.reload()} /> : this.props.children;
  }
}

function PublicContent({ children }: { children: (summary: ContentSummary) => ReactNode }) {
  const [summary, setSummary] = useState(initialContentSummary);
  const [error, setError] = useState('');
  useEffect(() => {
    if (summary) return;
    let alive = true;
    void loadContentSummary().then(value => { if (alive) setSummary(value); }).catch((caught: Error) => { if (alive) setError(caught.message); });
    return () => { alive = false; };
  }, [summary]);
  if (error) return <ErrorState message={error} retry={() => window.location.reload()} />;
  return summary ? children(summary) : <Loading />;
}

function LearningContent({ children }: { children: (bundle: ContentBundle) => ReactNode }) {
  const [bundle, setBundle] = useState<ContentBundle | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    void loadContent().then(value => { if (alive) setBundle(value); }).catch((caught: Error) => { if (alive) setError(caught.message); });
    return () => { alive = false; };
  }, []);
  if (error) return <ErrorState message={error} retry={() => window.location.reload()} />;
  return bundle ? children(bundle) : <Loading />;
}

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [accountAccess, setAccountAccess] = useState<AccountAccess | null>(null);
  const location = useLocation();
  const userId = session?.user.id; const accessToken = session?.access_token;
  const checkedAccess = accountAccess?.userId === userId ? accountAccess : null;
  const activeAccount = checkedAccess?.state === 'active' ? userId : null;
  useEffect(() => {
    if (userId && accessToken && activeAccount === userId) return startAutomaticSync(userId, accessToken);
  }, [userId, accessToken, activeAccount]);
  useEffect(() => {
    let alive = true; let revision = 0;
    const refresh = (invalidate = false) => {
      const request = ++revision;
      if (!userId || !accessToken) { setAccountAccess(null); return; }
      // A token refresh for the same account must not unmount an unsaved form.
      // An explicit account change invalidates the previous activation result.
      setAccountAccess(current => !invalidate && current?.userId === userId ? current : null);
      void api.accountStatus(accessToken).then(status => {
        if (alive && request === revision) setAccountAccess({ userId, state: status.active ? 'active' : 'inactive' });
      }).catch(() => {
        if (alive && request === revision) setAccountAccess({ userId, state: 'error' });
      });
    };
    const changed = () => refresh(true);
    refresh(); window.addEventListener('account-status-changed', changed);
    return () => { alive = false; window.removeEventListener('account-status-changed', changed); };
  }, [userId, accessToken]);
  useEffect(() => {
    let alive = true; let revision = 0;
    const accept = (next: Session | null) => { if (!alive) return; localStore.setAccount(next?.user.id || null); setSession(next); setAuthReady(true); };
    currentSession().then((next) => { if (!revision) accept(next); }).catch(() => { if (!revision) accept(null); });
    const subscription = supabase?.auth.onAuthStateChange((_event, next) => { revision++; accept(next); });
    return () => { alive = false; subscription?.data.subscription.unsubscribe(); };
  }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [location.pathname]);
  return <AppShell key={session?.user.id || 'guest'} session={session} preview={__CONTENT_MODE__ === 'preview'}><Seo /><RouteErrorBoundary key={location.pathname}><Suspense fallback={<Loading />}><Routes>
    <Route path="/" element={<PublicContent>{summary => <HomePage summary={summary} />}</PublicContent>} />
    <Route path="/poradniki" element={<GuidesPage />} />
    <Route path={halsArticle.path} element={<HalsArticlePage />} />
    <Route path={motorboatExamArticle.path} element={<MotorboatExamArticlePage />} />
    {(['SM', 'ZJ'] as const).map(track => <Route key={track} path={'/' + track.toLowerCase()} element={<PublicContent>{summary => <TrackPage summary={summary} track={track} />}</PublicContent>} />)}
    {(['SM', 'ZJ'] as const).map(track => <Route key={track} path={'/nauka/' + track.toLowerCase()} element={<PublicContent>{summary => <StudySetupPage summary={summary} track={track} />}</PublicContent>} />)}
    {(['SM', 'ZJ'] as const).map(track => <Route key={track} path={'/egzamin/' + track.toLowerCase()} element={<PublicContent>{summary => <ExamSetupPage summary={summary} track={track} authReady={authReady} />}</PublicContent>} />)}
    <Route path="/zasady" element={<LegalPage documentKey="terms" />} />
    <Route path="/prywatnosc" element={<LegalPage documentKey="privacy" />} />
    <Route path="/warunki-dodawania-pytan" element={<LegalPage documentKey="contribution" />} />
    <Route path="/cookies" element={<LegalPage documentKey="storage" />} />
    <Route path="/regulamin" element={<Navigate to="/zasady" replace />} />
    <Route path="/polityka-prywatnosci" element={<Navigate to="/prywatnosc" replace />} />
    <Route path="/dokumenty/archiwum" element={<LegalArchivePage />} />
    <Route path="/dokumenty/archiwum/:documentKey/:version" element={<LegalPage />} />
    <Route path="/kontakt" element={<ContactPage />} />
    <Route path="/o-projekcie" element={<StaticPage page="o-projekcie" />} />
    <Route path="/jak-to-dziala" element={<StaticPage page="jak-to-dziala" />} />
    <Route path="/napisz-artykul" element={<ArticleSubmissionPage />} />
    <Route path="/wesprzyj" element={<SupportPage />} />
    <Route path="/logowanie" element={<LoginPage />} />
    <Route path="/auth/callback" element={<AuthCallbackPage />} />
    <Route path="/usuwanie-konta" element={<DeletionPage />} />
    <Route path="/konto" element={!authReady ? <Loading /> : session ? <AccountPage session={session} /> : <Navigate to="/logowanie" replace />} />
    <Route path="*" element={<LearningRoutes session={session} authReady={authReady} access={checkedAccess?.state || 'loading'} />} />
  </Routes></Suspense></RouteErrorBoundary></AppShell>;
}

function LearningRoutes({ session, authReady, access }: { session: Session | null; authReady: boolean; access: AccountAccess['state'] | 'loading' }) {
  if (!authReady) return <Loading />;
  function accountRoute(page: ReactNode) {
    if (!session) return page;
    if (access === 'loading') return <Loading />;
    if (access === 'error') return <ErrorState message="Nie udało się sprawdzić stanu konta. Spróbuj ponownie." retry={() => window.dispatchEvent(new Event('account-status-changed'))} />;
    if (access === 'inactive') return <Navigate to="/konto" replace />;
    return page;
  }
  return <Routes>
    <Route path="/nauka/sm/sesja" element={<LearningContent>{bundle => <StudySessionPage bundle={bundle} track="SM" session={session} />}</LearningContent>} /><Route path="/nauka/zj/sesja" element={<LearningContent>{bundle => <StudySessionPage bundle={bundle} track="ZJ" session={session} />}</LearningContent>} />
    <Route path="/nauka/sm/koniec" element={<StudyDonePage track="SM" />} /><Route path="/nauka/zj/koniec" element={<StudyDonePage track="ZJ" />} />
    <Route path="/egzamin/sesja/:attemptId" element={<LearningContent>{bundle => <ExamSessionPage bundle={bundle} />}</LearningContent>} /><Route path="/egzamin/wynik/:attemptId" element={<LearningContent>{bundle => <ExamResultPage bundle={bundle} session={session} />}</LearningContent>} />
    <Route path="/dashboard" element={accountRoute(<LearningContent>{bundle => <DashboardPage bundle={bundle} session={session} />}</LearningContent>)} /><Route path="/dodaj-pytanie" element={accountRoute(<LearningContent>{bundle => <ContributorPage bundle={bundle} session={session} />}</LearningContent>)} />
    <Route path="/panel" element={accountRoute(<LearningContent>{bundle => <OwnerPage bundle={bundle} session={session} />}</LearningContent>)} />
    <Route path="*" element={<NotFoundPage />} />
  </Routes>;
}

export default App;
